import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, Search, ShoppingCart } from 'lucide-react';
import './UsersManagement.css';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOrders, setUserOrders] = useState([]);
  const navigate = useNavigate();

  const fetchUsers = useCallback(async () => {
    try {
      const url = searchQuery 
        ? `${API}/api/admin/users?search=${searchQuery}`
        : `${API}/api/admin/users`;
      const response = await axios.get(url, { withCredentials: true });
      setUsers(response.data.users);
    } catch (error) {
      if (error.response?.status === 403) {
        alert('Admin access required');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [searchQuery, navigate]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const viewUserOrders = async (user) => {
    try {
      const response = await axios.get(`${API}/api/admin/users/${user.id}/orders`, {
        withCredentials: true
      });
      setUserOrders(response.data.orders);
      setSelectedUser(user);
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to fetch user orders');
    }
  };

  if (loading) {
    return <div className="admin-loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="users-management">
      <div className="page-header">
        <h1>Users Management</h1>
        <form onSubmit={handleSearch} className="search-form">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Verified</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  <Users size={48} />
                  <p>No users found</p>
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge ${user.role}`}>
                      {user.role || 'user'}
                    </span>
                  </td>
                  <td>
                    <span className={`verified-badge ${user.isVerified ? 'yes' : 'no'}`}>
                      {user.isVerified ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="view-orders-btn"
                      onClick={() => viewUserOrders(user)}
                    >
                      <ShoppingCart size={16} />
                      View Orders
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedUser.name}&apos;s Orders</h2>
              <button className="modal-close" onClick={() => setSelectedUser(null)}>×</button>
            </div>
            <div className="orders-list-modal">
              {userOrders.length === 0 ? (
                <p className="no-orders">No orders found for this user</p>
              ) : (
                userOrders.map(order => (
                  <div key={order.id} className="order-item-modal">
                    <div>
                      <strong>Order #{order.id.slice(0, 8).toUpperCase()}</strong>
                      <p>{new Date(order.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p>₹{order.totalAmount || order.price}</p>
                      <span className="order-status-badge">{order.orderStatus}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;